import { FC, useMemo, useState } from "react";
import { Button, IconButton, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileFilled from "@mui/icons-material/UploadFile";
import { models } from "types/api/viewModels";
import { getFormFieldProps } from "utils/form";
import { v4 as uuidv4 } from "uuid";
import {
	AttachFile,
	CommentSectionHeaderText,
	CommentsInfoLabel,
	CommentsInfoText,
	CommentsText,
	CommentTextareaPlaceholder,
	EmptyCommentsPlaceholderText,
	SendCommentText
} from "constants/text";
import CommentCard from "./CommentCard";
import { COMMENT_MAX_LENGTH, CommentFormFields, commentFormSchema } from "./schema";
import {
	CommentsInfo,
	CommentsSection,
	CommentsSectionHeader,
	CustomQuoteCommentsFlyoutContainer,
	CustomQuoteCommentsFlyoutContent,
	CustomQuoteCommentsFlyoutFooter,
	CustomQuoteCommentsFlyoutHeader,
	CustomQuoteCommentsFlyoutTitle,
	EmptyCommentsPlaceholder
} from "./CustomQuoteCommentsFlyout.styles";
import { useAddCommentMutation, useGetCustomQuoteByIdQuery } from "features/api/customQuotesApi.ts";
import UploadModal, { ErrorableFileState } from "components/UploadModal/UploadModal.tsx";
import { uploadFileToBlob } from "utils/blob.ts";
import useBlobStorage from "hooks/useBlobStorage.ts";
import { sanitizeBlobName } from "utils/file.ts";
import { ReplacementFile } from "features/reducers/replacementOrder/cart.ts";
import UploadView from "components/UploadView/UploadView.tsx";

interface CustomQuoteCommentsFlyoutProps {
	quote: models["CustomQuoteViewModel"] | undefined;
	closeDrawer: () => void;
}

const CustomQuoteCommentsFlyout: FC<CustomQuoteCommentsFlyoutProps> = ({ quote, closeDrawer }) => {
	// Use RTK Query to get real-time quote data updates
	const { data: currentQuote } = useGetCustomQuoteByIdQuery(quote?.quoteId ?? "");
	const [uploadedFiles, setUploadedFiles] = useState<ReplacementFile[]>([]);
	const [open, setOpen] = useState(false);
	const { uploadContainerBlob } = useBlobStorage();

	// Use the real-time quote data if available, fallback to prop
	const activeQuote = currentQuote || quote;
	const hasComments = activeQuote?.comments && activeQuote.comments.length > 0;

	const [addComment] = useAddCommentMutation();

	const { register, handleSubmit, formState, watch, reset } = useForm<CommentFormFields>({
		resolver: yupResolver(commentFormSchema),
		defaultValues: {
			commentText: ""
		}
	});

	const commentText = watch("commentText") || "";
	const formFieldData = { formState, register, schema: commentFormSchema };

	const uploadCommentFiles = async (requestId: string, files: ReplacementFile[]): Promise<string[]> => {
		const uploadedFiles: string[] = [];
		var commentId = uuidv4();
		for (let i = 0; i < files.length; i++) {
			try {
				const file = files[i];
				if (!file) continue;
				const sanitizedFileName = sanitizeBlobName(file.name);
				const fileName = `comments/${commentId}/${sanitizedFileName}`;
				await uploadFileToBlob({
					fileUrl: file.fileUrl,
					fileName,
					size: file.size,
					requestId,
					uploadContainerBlob
				});
				uploadedFiles.push(fileName);
			} catch (error) {
				console.error("File upload failed:", error);
			}
		}
		return uploadedFiles;
	};

	const onSubmit = async (data: CommentFormFields) => {
		const attachmentUrls: string[] =
			uploadedFiles.length > 0 ? await uploadCommentFiles(quote?.quoteId!, uploadedFiles) : [];

		const apiPayload = {
			id: activeQuote?.quoteId!,
			comment: {
				comment: data.commentText,
				attachmentUrls: attachmentUrls
			}
		};
		try {
			await addComment(apiPayload).unwrap();
			setUploadedFiles([]);
		} catch (error: any) {
			console.error("Failed to add comment:", error);
		}
		reset(); // Clear form after submission
	};

	const dummyItem = { id: "custom-quote-upload" } as any;
	const MAX_FILES = 3;
	const remainingFiles = Math.max(0, MAX_FILES - uploadedFiles.length);

	const handleClose = (files: ErrorableFileState[]) => {
		const validFiles = files.map(({ file }, index) => {
			return {
				name: file.name,
				size: file.size,
				id: `${file}-${index}`,
				fileUrl: URL.createObjectURL(file),
				originalLineItemId: ""
			} as ReplacementFile;
		});
		setUploadedFiles((prev) => [...prev, ...validFiles]);
		setOpen(false);
	};

	const handleOpen = () => {
		setOpen(true);
	};

	const renderAttachedFiles = useMemo(() => {
		const removeFile = (index: number) => {
			const newFiles = uploadedFiles.filter((_, i) => i !== index);
			setUploadedFiles(newFiles);
		};
		if (uploadedFiles.length === 0) return <></>;
		return uploadedFiles.map((file, index) => {
			const fileName = file.name.split("/").slice(-1).join();
			return (
				<UploadView
					key={`${file.name}-${index}`}
					index={index}
					file={{
						name: file.name,
						size: file.size,
						fileUrl: file.fileUrl,
						id: `file-${index}`,
						originalLineItemId: ""
					}}
					onImageRemove={() => removeFile(index)}
				/>
			);
		});
	}, [uploadedFiles]);

	return (
		<CustomQuoteCommentsFlyoutContainer data-testid="custom-quote-comments-flyout">
			<CustomQuoteCommentsFlyoutHeader>
				<CustomQuoteCommentsFlyoutTitle>
					<span>{CommentsText}</span>
				</CustomQuoteCommentsFlyoutTitle>
				<IconButton
					onClick={closeDrawer}
					data-testid="custom-quote-comments-flyout-close-button"
					aria-label="Close comments"
					size="small"
				>
					<CloseIcon />
				</IconButton>
			</CustomQuoteCommentsFlyoutHeader>

			<CommentsInfo>
				<span>{CommentsInfoLabel}</span> {CommentsInfoText}
			</CommentsInfo>

			<CustomQuoteCommentsFlyoutContent>
				<CommentsSection>
					<CommentsSectionHeader>
						<span>{CommentSectionHeaderText}</span>
					</CommentsSectionHeader>
					{hasComments ? (
						<>
							{activeQuote.comments?.map((comment, index) => (
								<CommentCard
									key={`${comment.timestamp}-${comment.userId}-${comment.comment?.substring(0, 20)}-${index}`}
									comment={comment}
									quote={quote}
								/>
							))}
						</>
					) : (
						<EmptyCommentsPlaceholder>
							<span>{EmptyCommentsPlaceholderText}</span>
						</EmptyCommentsPlaceholder>
					)}
				</CommentsSection>

				<CustomQuoteCommentsFlyoutFooter>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Button
							fullWidth
							variant="outlined"
							startIcon={<UploadFileFilled />}
							data-testid="custom-quote-attach-file-button"
							onClick={handleOpen}
							type="button"
						>
							{AttachFile}
						</Button>
						{renderAttachedFiles}
						<TextField
							{...getFormFieldProps({
								name: "commentText",
								characterLimit: {
									currentLength: commentText.length,
									limit: COMMENT_MAX_LENGTH
								},
								...formFieldData
							})}
							fullWidth
							multiline
							size="small"
							placeholder={CommentTextareaPlaceholder}
							maxRows={5}
							minRows={1}
							data-testid="custom-quote-add-comment-input"
						/>

						<Button
							fullWidth
							variant="contained"
							type="submit"
							data-testid="custom-quote-send-comment-button"
						>
							{SendCommentText}
						</Button>
					</form>
				</CustomQuoteCommentsFlyoutFooter>
				<UploadModal
					handleClose={handleClose}
					item={dummyItem}
					open={open}
					maxFiles={remainingFiles}
					fileUploads={[]}
					fileSelectionText={null}
				/>
			</CustomQuoteCommentsFlyoutContent>
		</CustomQuoteCommentsFlyoutContainer>
	);
};

export default CustomQuoteCommentsFlyout;
